// src/pages/UpdateURD.jsx
import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setBranchList } from "../slices/branchSlice";

import { useGetUrdByIdQuery } from "../toolkitServices/urdApi";
import URDForm from "../components/urd/URDForm";
import { convertToDateInputFormat } from "../config/helperFunctions";

// tiny toast
const toast = (msg, type = "error") => {
  if (window?.toastr?.[type]) return window.toastr[type](msg);
  if (window?.toast?.[type]) return window.toast[type](msg);
  if (window?.notify) return window.notify(msg, { type });
  alert(msg);
};

// generic opt for non-customer (e.g., Branch)
const toOpt = (obj, labelKey = "name", valueKey = "_id", fallback = "") => {
  if (!obj) return null;
  if (typeof obj === "string") return { value: obj, label: String(fallback || obj) };
  const label =
    obj[labelKey] ||
    obj.branchName ||
    obj.customerName ||
    obj.companyName ||
    fallback ||
    "";
  const value = obj[valueKey] ?? obj._id ?? "";
  return { value, label: String(label) };
};

// customer option WITH info (so hover card works in edit mode)
const makeCustomerOptFromDoc = (c) => {
  if (!c) return null;

  if (typeof c === "string") {
    return {
      value: c,
      label: String(c),
      info: {
        _id: c,
        customerName: "",
        customerId: "",
        contact: "",
        email: "",
        addressLine1: "",
        city: "",
        state: "",
        country: "",
        createdAt: "",
        fineGold: 0,
        fineSilver: 0,
        balanceAmount: 0,
      },
    };
  }

  const label =
    c.customerName ||
    c.customerId ||
    c.contact ||
    "Customer";

  return {
    value: c._id ?? "",
    label: String(label),
    info: {
      _id: c._id ?? "",
      customerName: c.customerName ?? "",
      customerId: c.customerId ?? "",
      contact: c.contact ?? "",
      email: c.email ?? "",
      addressLine1: c.addressLine1 ?? "",
      city: c.city ?? "",
      state: c.state ?? "",
      country: c.country ?? "",
      createdAt: c.createdAt ?? "",
      fineGold: c.fineGold ?? 0,
      fineSilver: c.fineSilver ?? 0,
      balanceAmount: c.balanceAmount ?? 0,
    },
  };
};

const toFixedOrEmpty = (v, d = 3) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : "";
};

const mapDocToForm = (doc) => {
  if (!doc) return null;

  // ⭐ customerId populated ho ya controller se customerInfo aaye — dono handle
  const customer = makeCustomerOptFromDoc(doc.customerId || doc.customerInfo);
  const branch = toOpt(doc.branchId, "branchName");
  const counter = toOpt(doc.counterId, "counterName");

  const urdDate = doc.urdDate ? convertToDateInputFormat(doc.urdDate) : "";

  const urdEntries = (doc.entries || []).map((e) => ({
    category: e.categoryId
      ? {
          value: e.categoryId?._id ?? e.categoryId,
          label: e.categoryName || e.categoryId?.categoryName || "",
        }
      : null,
    product: e.productId
      ? {
          value: e.productId?._id ?? e.productId,
          label: e.productName || e.productId?.productName || "",
        }
      : null,

    qty: String(e.qty ?? ""),
    totalWt: toFixedOrEmpty(e.totalWt),
    packingWt: toFixedOrEmpty(e.packingWt),
    grossWt: toFixedOrEmpty(e.grossWt),
    stoneWt: toFixedOrEmpty(e.stoneWt),
    diamondWt: toFixedOrEmpty(e.diamondWt),
    netWt: toFixedOrEmpty(e.netWt),
    finePercentage: String(e.finePercentage ?? ""),
    fineWt: toFixedOrEmpty(e.fineWt),
    ratePerGram: String(e.ratePerGram ?? ""),
    amount: Number.isFinite(+e.amount) ? (+e.amount).toFixed(2) : "",
    urdDescription: e.urdDescription || "",
  }));

  const payments = (doc.payments || []).map((p) => ({
    type: "Payout",
    mode: p.mode || "Cash",
    amount: Number.isFinite(+p.amount) ? (+p.amount).toFixed(2) : "",
    refNo: p.refNo || "",
    description: p.description || "",
    sourceType: "URD",
  }));

  return {
    _id: doc._id,
    customer, // <-- includes info
    branch,
    counter,
    urdNo: String(doc.urdNo ?? ""),
    urdDate,
    settlement: doc.settlement || "Wallet",
    notes: doc.notes || "",
    urdEntries,
    payments,
  };
};

const UpdateURD = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // masters
  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery({ page: 1, limit: 1000, search: "" });
  const { data: products } = useGetAllProductsQuery({ page: 1, limit: 2000, search: "" });

  useEffect(() => {
    if (categories?.data?.length) dispatch(setCategoryList(categories.data));
    if (products?.data?.length) dispatch(setProductList(products.data));
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
  }, [dispatch, categories, products, branches]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const { data, isFetching, isLoading, isError, error, refetch } = useGetUrdByIdQuery(id, {
    skip: !id,
  });

  const existingData = useMemo(() => {
    const doc = data?.data ?? data;
    return doc ? mapDocToForm(doc) : null;
  }, [data]);

  if (isLoading || isFetching) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-6 w-40 bg-slate-200 rounded mb-4" />
        <div className="animate-pulse h-96 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  if (isError) {
    const msg = error?.data?.message || error?.error || "Failed to load URD";
    return (
      <div className="p-6">
        <div className="text-red-600 font-medium mb-2">{msg}</div>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 rounded border hover:bg-gray-100"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!existingData) {
    return <div className="p-6 text-slate-600">No data found.</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="max-w-8xl mx-auto rounded-xl relative max-h-[90vh] overflow-y-auto"
    >
      <URDForm existingData={existingData} mode="edit" />
    </motion.div>
  );
};

export default UpdateURD;
